import { PromoCodeForm } from "@/components/forms/promo-code-form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { DiscountType, PromoCodeDataSchema, type PromoCodeData } from "@/core/entities/promo-code";
import { ProductType } from "@/core/entities/product";
import { useCreatePromoCode } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { removeUndefinedValues } from "@/utils/remove-undefined-values";

interface PromoCodeCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PromoCodeCreateDialog = ({
  open,
  onOpenChange,
}: PromoCodeCreateDialogProps) => {
  const [showCloseAlert, setShowCloseAlert] = useState(false);

  const createPromoCodeMutation = useCreatePromoCode();

  const form = useForm<PromoCodeData>({
    resolver: zodResolver(PromoCodeDataSchema),
    defaultValues: {
      name: "",
      code: "",
      discount: {
        type: DiscountType.PERCENTAGE,
        value: 0,
      },
      expirationDate: undefined,
      enabledFor: ProductType.BOTH,
    },
  });

  const onSubmit = async () => {
    const formData = form.getValues();
    const cleanFormData = removeUndefinedValues(formData);
    const data = PromoCodeDataSchema.parse(cleanFormData);
    try {
      await createPromoCodeMutation.mutateAsync({ data });
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to create promo code:", error);
      throw error;
    }
  };

  const handleClose = () => {
    if (form.formState.isDirty) {
      setShowCloseAlert(true);
    } else {
      onOpenChange(false);
    }
  };

  const confirmClose = () => {
    setShowCloseAlert(false);
    onOpenChange(false);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={handleClose}>
        <SheetContent
          side="right"
          className="w-full md:max-w-2xl max-w-full overflow-y-auto p-4 sm:p-8 md:p-12"
        >
          <SheetHeader className="p-0">
            <SheetTitle>Create New Promo Code</SheetTitle>
            <SheetDescription>
              Add a new promo code. Fill in all the required information below.
            </SheetDescription>
          </SheetHeader>
          <PromoCodeForm
            form={form}
            onCancel={handleClose}
            onSubmit={onSubmit}
            isLoading={createPromoCodeMutation.isPending}
            submitText="Create Promo Code"
          />
        </SheetContent>
      </Sheet>

      <AlertDialog open={showCloseAlert} onOpenChange={setShowCloseAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to close without
              saving?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continue editing</AlertDialogCancel>
            <AlertDialogAction onClick={confirmClose}>
              Close without saving
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
